/**
 * The bundled corpus.
 *
 * Every line here is **public domain** — folk proverbs, or authors dead long enough that their
 * work has entered the public domain in the EU (life + 70 years) and Russia. Nothing is scraped
 * from a meme site, nothing is a song lyric, and nothing is by a living author. That is a
 * deliberate constraint, not an accident: an app whose whole trick is quoting other people has
 * to be able to say where every line came from.
 *
 * Proverbs carry no named author by nature, which also makes them immune to the
 * misattribution that plagues quote collections online. Where a line is attributed to a person,
 * it is one whose wording is well established.
 *
 * `tags` are topic keywords in the entry's own language, written as ordinary words — the
 * matcher stems them at load time, so `работа` also matches `работаю` and `работе`.
 *
 * Where Russian drops a vowel from the stem (`деньги` / `денег`, `сон` / `сна`, `день` / `дня`)
 * a suffix-based stemmer cannot follow it, so both forms are listed as tags. That is a
 * deliberate, documented limit of the matcher, not an oversight in the data.
 */

import type { CorpusEntry } from '../core/types.js';

const PD = 'public domain';

/** Shorthand for the many folk proverbs, which share an attribution shape. */
function proverb(lang: 'ru' | 'en'): CorpusEntry['attribution'] {
  return { author: lang === 'ru' ? 'Русская пословица' : 'English proverb', licence: PD };
}

export const ENTRIES: CorpusEntry[] = [
  // ============================ RUSSIAN ============================
  {
    id: 'ru-rabota-volk',
    lang: 'ru',
    text: 'Работа не волк, в лес не убежит.',
    tags: ['работа', 'дело', 'труд', 'лень', 'завтра', 'откладывать', 'дедлайн', 'офис'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-rabota-durakov',
    lang: 'ru',
    text: 'От работы кони дохнут.',
    tags: ['работа', 'устал', 'усталость', 'уставать', 'переработка', 'выгорание', 'труд'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-prutkov-nelzya',
    lang: 'ru',
    text: 'Нельзя объять необъятное.',
    tags: ['задача', 'работа', 'много', 'всё', 'успеть', 'план', 'проект'],
    attribution: { author: 'Козьма Прутков', work: 'Плоды раздумья', licence: PD },
  },
  {
    id: 'ru-prutkov-koren',
    lang: 'ru',
    text: 'Зри в корень!',
    tags: ['проблема', 'причина', 'баг', 'ошибка', 'почему', 'разобраться', 'суть'],
    attribution: { author: 'Козьма Прутков', work: 'Плоды раздумья', licence: PD },
  },
  {
    id: 'ru-prutkov-specialist',
    lang: 'ru',
    text: 'Специалист подобен флюсу: полнота его односторонняя.',
    tags: ['эксперт', 'специалист', 'знание', 'опыт', 'профессия', 'работа'],
    attribution: { author: 'Козьма Прутков', work: 'Плоды раздумья', licence: PD },
  },
  {
    id: 'ru-prutkov-schastie',
    lang: 'ru',
    text: 'Хочешь быть счастливым — будь им.',
    tags: ['счастье', 'настроение', 'грусть', 'радость', 'жизнь', 'совет'],
    attribution: { author: 'Козьма Прутков', work: 'Плоды раздумья', licence: PD },
  },
  {
    id: 'ru-prutkov-bditelnost',
    lang: 'ru',
    text: 'Бди!',
    tags: ['внимание', 'осторожно', 'опасность', 'риск', 'проверка', 'тест'],
    attribution: { author: 'Козьма Прутков', work: 'Плоды раздумья', licence: PD },
  },
  {
    id: 'ru-utro-vechera',
    lang: 'ru',
    text: 'Утро вечера мудренее.',
    tags: ['сон', 'сна', 'спать', 'усталость', 'устал', 'решение', 'вечер', 'утро', 'ночь', 'думать'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-semь-raz',
    lang: 'ru',
    text: 'Семь раз отмерь, один раз отрежь.',
    tags: ['решение', 'спешка', 'ошибка', 'план', 'проверка', 'деплой', 'релиз'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-pospeshish',
    lang: 'ru',
    text: 'Поспешишь — людей насмешишь.',
    tags: ['спешка', 'быстро', 'срочно', 'дедлайн', 'ошибка', 'торопиться'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-bez-truda',
    lang: 'ru',
    text: 'Без труда не выловишь и рыбку из пруда.',
    tags: ['труд', 'работа', 'усилие', 'лень', 'результат', 'учёба', 'спорт'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-appetit',
    lang: 'ru',
    text: 'Аппетит приходит во время еды.',
    tags: ['еда', 'голод', 'обед', 'кушать', 'начать', 'мотивация', 'ужин'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-schi-da-kasha',
    lang: 'ru',
    text: 'Щи да каша — пища наша.',
    tags: ['еда', 'обед', 'ужин', 'голод', 'простота', 'завтрак'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-den-kormit',
    lang: 'ru',
    text: 'Кто рано встаёт, тому бог подаёт.',
    tags: ['утро', 'рано', 'подъём', 'будильник', 'сон', 'понедельник'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-ponedelnik',
    lang: 'ru',
    text: 'Понедельник — день тяжёлый.',
    tags: ['понедельник', 'неделя', 'работа', 'утро', 'тяжело', 'начало'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-den-goda',
    lang: 'ru',
    text: 'Делу время, потехе час.',
    tags: ['время', 'работа', 'отдых', 'развлечение', 'баланс', 'выходные'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-chehov-kratkost',
    lang: 'ru',
    text: 'Краткость — сестра таланта.',
    tags: ['текст', 'слова', 'письмо', 'встреча', 'длинно', 'объяснение', 'презентация'],
    attribution: { author: 'А. П. Чехов', work: 'Письмо брату, 1889', licence: PD },
  },
  {
    id: 'ru-chehov-vsyo-prekrasno',
    lang: 'ru',
    text: 'В человеке всё должно быть прекрасно: и лицо, и одежда, и душа, и мысли.',
    tags: ['внешность', 'одежда', 'человек', 'красота', 'стиль', 'дизайн'],
    attribution: { author: 'А. П. Чехов', work: 'Дядя Ваня', licence: PD },
  },
  {
    id: 'ru-gogol-dorogi',
    lang: 'ru',
    text: 'В России две беды: дураки и дороги.',
    tags: ['проблема', 'беда', 'дурак', 'дорога', 'россия', 'жалоба'],
    attribution: { author: 'Приписывается Н. В. Гоголю', licence: PD },
  },
  {
    id: 'ru-ilf-petrov-utopayushchie',
    lang: 'ru',
    text: 'Спасение утопающих — дело рук самих утопающих.',
    tags: ['помощь', 'сам', 'проблема', 'поддержка', 'работа', 'один'],
    attribution: { author: 'И. Ильф и Е. Петров', work: 'Двенадцать стульев', licence: PD },
  },
  {
    id: 'ru-ilf-petrov-avto',
    lang: 'ru',
    text: 'Автомобиль — не роскошь, а средство передвижения.',
    tags: ['машина', 'автомобиль', 'дорога', 'покупка', 'деньги', 'транспорт'],
    attribution: { author: 'И. Ильф и Е. Петров', work: 'Золотой телёнок', licence: PD },
  },
  {
    id: 'ru-ilf-petrov-lyod',
    lang: 'ru',
    text: 'Лёд тронулся, господа присяжные заседатели!',
    tags: ['начало', 'прогресс', 'результат', 'наконец', 'сдвиг', 'проект', 'релиз'],
    attribution: { author: 'И. Ильф и Е. Петров', work: 'Двенадцать стульев', licence: PD },
  },
  {
    id: 'ru-ilf-petrov-utro-dengi',
    lang: 'ru',
    text: 'Утром деньги — вечером стулья.',
    tags: ['деньги', 'денег', 'деньгами', 'оплата', 'счёт', 'зарплата', 'договор', 'сделка', 'цена'],
    attribution: { author: 'И. Ильф и Е. Петров', work: 'Двенадцать стульев', licence: PD },
  },
  {
    id: 'ru-dengi-schet',
    lang: 'ru',
    text: 'Деньги счёт любят.',
    tags: ['деньги', 'денег', 'счёт', 'бюджет', 'цена', 'зарплата', 'экономия', 'трата', 'стоит'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-kopeika',
    lang: 'ru',
    text: 'Копейка рубль бережёт.',
    tags: ['деньги', 'денег', 'экономия', 'бюджет', 'трата', 'мелочь', 'копить', 'стоит', 'дорого'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-drug-poznayotsya',
    lang: 'ru',
    text: 'Друг познаётся в беде.',
    tags: ['друг', 'друзья', 'дружба', 'помощь', 'беда', 'проблема', 'поддержка', 'команда'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-odna-golova',
    lang: 'ru',
    text: 'Одна голова хорошо, а две лучше.',
    tags: ['команда', 'помощь', 'совет', 'решение', 'вместе', 'коллега', 'ревью'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-lyubov-zla',
    lang: 'ru',
    text: 'Любовь зла — полюбишь и козла.',
    tags: ['любовь', 'отношения', 'свидание', 'чувства', 'сердце'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-nasilno-mil',
    lang: 'ru',
    text: 'Насильно мил не будешь.',
    tags: ['любовь', 'отношения', 'отказ', 'уговорить', 'убедить', 'продажа'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-pervy-blin',
    lang: 'ru',
    text: 'Первый блин комом.',
    tags: ['ошибка', 'неудача', 'первый', 'попытка', 'провал', 'тест', 'баг'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-na-oshibkah',
    lang: 'ru',
    text: 'На ошибках учатся.',
    tags: ['ошибка', 'учёба', 'опыт', 'неудача', 'баг', 'провал'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-terpenie-trud',
    lang: 'ru',
    text: 'Терпение и труд всё перетрут.',
    tags: ['терпение', 'труд', 'упорство', 'сложно', 'трудно', 'учёба'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-volkov-boyatsya',
    lang: 'ru',
    text: 'Волков бояться — в лес не ходить.',
    tags: ['страх', 'риск', 'бояться', 'решиться', 'попробовать', 'смелость'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-glaza-boyatsya',
    lang: 'ru',
    text: 'Глаза боятся, а руки делают.',
    tags: ['страх', 'задача', 'много', 'начать', 'сложно', 'работа'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-v-gostyah-doma',
    lang: 'ru',
    text: 'В гостях хорошо, а дома лучше.',
    tags: ['дом', 'путешествие', 'отпуск', 'поездка', 'вернуться', 'уют'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-koshka-myshka',
    lang: 'ru',
    text: 'Кошке игрушки, а мышке слёзки.',
    tags: ['кот', 'кошка', 'мышь', 'игра', 'шутка', 'смешно', 'животное'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-sobaka-lay',
    lang: 'ru',
    text: 'Собака лает — караван идёт.',
    tags: ['критика', 'мнение', 'собака', 'шум', 'игнорировать', 'комментарий'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-zdorovie',
    lang: 'ru',
    text: 'В здоровом теле — здоровый дух.',
    tags: ['здоровье', 'спорт', 'бег', 'зал', 'тренировка', 'тело', 'болезнь'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-slovo-vorobei',
    lang: 'ru',
    text: 'Слово не воробей: вылетит — не поймаешь.',
    tags: ['слово', 'разговор', 'сказать', 'обещание', 'молчание', 'сообщение'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-molchanie-zoloto',
    lang: 'ru',
    text: 'Слово — серебро, молчание — золото.',
    tags: ['молчание', 'разговор', 'встреча', 'слово', 'тишина', 'спор'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-pogoda-net',
    lang: 'ru',
    text: 'У природы нет плохой погоды.',
    tags: ['погода', 'дождь', 'снег', 'холодно', 'жарко', 'зима', 'лето'],
    attribution: proverb('ru'),
  },
  {
    id: 'ru-gotov-sani',
    lang: 'ru',
    text: 'Готовь сани летом, а телегу зимой.',
    tags: ['план', 'подготовка', 'зима', 'лето', 'заранее', 'время'],
    attribution: proverb('ru'),
  },

  // ============================ ENGLISH ============================
  {
    id: 'en-twain-quit-smoking',
    lang: 'en',
    text: 'Giving up smoking is the easiest thing in the world. I know because I have done it thousands of times.',
    tags: ['habit', 'quit', 'smoking', 'discipline', 'diet', 'resolution', 'again'],
    attribution: { author: 'Mark Twain', licence: PD },
  },
  {
    id: 'en-twain-tomorrow',
    lang: 'en',
    text: 'Never put off till tomorrow what may be done day after tomorrow just as well.',
    tags: ['procrastination', 'tomorrow', 'later', 'deadline', 'lazy', 'work', 'task'],
    attribution: { author: 'Mark Twain', licence: PD },
  },
  {
    id: 'en-twain-truth',
    lang: 'en',
    text: 'If you tell the truth, you do not have to remember anything.',
    tags: ['truth', 'lie', 'honest', 'memory', 'story', 'excuse'],
    attribution: { author: 'Mark Twain', licence: PD },
  },
  {
    id: 'en-twain-banker',
    lang: 'en',
    text: 'A banker is a fellow who lends you his umbrella when the sun is shining and wants it back the minute it begins to rain.',
    tags: ['money', 'bank', 'loan', 'credit', 'help', 'rain', 'weather'],
    attribution: { author: 'Mark Twain', licence: PD },
  },
  {
    id: 'en-twain-report',
    lang: 'en',
    text: 'The reports of my death are greatly exaggerated.',
    tags: ['rumour', 'news', 'gossip', 'wrong', 'exaggeration', 'dead', 'broken'],
    attribution: { author: 'Mark Twain', licence: PD },
  },
  {
    id: 'en-wilde-temptation',
    lang: 'en',
    text: 'I can resist everything except temptation.',
    tags: ['temptation', 'diet', 'cake', 'chocolate', 'shopping', 'buy', 'discipline'],
    attribution: { author: 'Oscar Wilde', work: 'Lady Windermere’s Fan', licence: PD },
  },
  {
    id: 'en-wilde-experience',
    lang: 'en',
    text: 'Experience is simply the name we give our mistakes.',
    tags: ['mistake', 'experience', 'bug', 'failure', 'learn', 'wrong'],
    attribution: { author: 'Oscar Wilde', work: 'The Picture of Dorian Gray', licence: PD },
  },
  {
    id: 'en-wilde-advice',
    lang: 'en',
    text: 'The only thing to do with good advice is to pass it on. It is never of any use to oneself.',
    tags: ['advice', 'help', 'suggestion', 'review', 'feedback', 'opinion'],
    attribution: { author: 'Oscar Wilde', work: 'An Ideal Husband', licence: PD },
  },
  {
    id: 'en-wilde-work',
    lang: 'en',
    text: 'Work is the curse of the drinking classes.',
    tags: ['work', 'office', 'job', 'monday', 'beer', 'friday', 'drink'],
    attribution: { author: 'Oscar Wilde', licence: PD },
  },
  {
    id: 'en-bierce-bore',
    lang: 'en',
    text: 'Bore: a person who talks when you wish him to listen.',
    tags: ['meeting', 'talk', 'boring', 'listen', 'call', 'colleague', 'conversation'],
    attribution: { author: 'Ambrose Bierce', work: 'The Devil’s Dictionary', licence: PD },
  },
  {
    id: 'en-bierce-future',
    lang: 'en',
    text: 'Future: that period of time in which our affairs prosper, our friends are true and our happiness is assured.',
    tags: ['future', 'plan', 'hope', 'next', 'year', 'roadmap', 'someday'],
    attribution: { author: 'Ambrose Bierce', work: 'The Devil’s Dictionary', licence: PD },
  },
  {
    id: 'en-bierce-patience',
    lang: 'en',
    text: 'Patience: a minor form of despair, disguised as a virtue.',
    tags: ['patience', 'wait', 'waiting', 'slow', 'queue', 'loading', 'delay'],
    attribution: { author: 'Ambrose Bierce', work: 'The Devil’s Dictionary', licence: PD },
  },
  {
    id: 'en-bierce-corporation',
    lang: 'en',
    text: 'Corporation: an ingenious device for obtaining individual profit without individual responsibility.',
    tags: ['company', 'corporation', 'business', 'boss', 'management', 'office', 'responsibility'],
    attribution: { author: 'Ambrose Bierce', work: 'The Devil’s Dictionary', licence: PD },
  },
  {
    id: 'en-shaw-progress',
    lang: 'en',
    text: 'The reasonable man adapts himself to the world; the unreasonable one persists in trying to adapt the world to himself. Therefore all progress depends on the unreasonable man.',
    tags: ['progress', 'change', 'stubborn', 'idea', 'innovation', 'startup', 'reason'],
    attribution: { author: 'George Bernard Shaw', work: 'Man and Superman', licence: PD },
  },
  {
    id: 'en-shaw-communication',
    lang: 'en',
    text: 'The single biggest problem in communication is the illusion that it has taken place.',
    tags: ['communication', 'meeting', 'email', 'message', 'misunderstanding', 'team', 'talk'],
    attribution: { author: 'Attributed to George Bernard Shaw', licence: PD },
  },
  {
    id: 'en-jerome-work',
    lang: 'en',
    text: 'I like work: it fascinates me. I can sit and look at it for hours.',
    tags: ['work', 'lazy', 'task', 'todo', 'procrastination', 'office', 'backlog'],
    attribution: { author: 'Jerome K. Jerome', work: 'Three Men in a Boat', licence: PD },
  },
  {
    id: 'en-franklin-early',
    lang: 'en',
    text: 'Early to bed and early to rise makes a man healthy, wealthy and wise.',
    tags: ['sleep', 'morning', 'early', 'alarm', 'bed', 'tired', 'monday'],
    attribution: { author: 'Benjamin Franklin', work: 'Poor Richard’s Almanack', licence: PD },
  },
  {
    id: 'en-franklin-taxes',
    lang: 'en',
    text: 'In this world nothing can be said to be certain, except death and taxes.',
    tags: ['tax', 'money', 'certain', 'death', 'invoice', 'bill', 'government'],
    attribution: { author: 'Benjamin Franklin', licence: PD },
  },
  {
    id: 'en-franklin-time',
    lang: 'en',
    text: 'Lost time is never found again.',
    tags: ['time', 'late', 'waste', 'deadline', 'hurry', 'schedule'],
    attribution: { author: 'Benjamin Franklin', work: 'Poor Richard’s Almanack', licence: PD },
  },
  {
    id: 'en-proverb-pudding',
    lang: 'en',
    text: 'The proof of the pudding is in the eating.',
    tags: ['test', 'proof', 'demo', 'result', 'try', 'food', 'evidence'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-rome',
    lang: 'en',
    text: 'Rome was not built in a day.',
    tags: ['time', 'patience', 'project', 'slow', 'progress', 'build', 'deadline'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-eggs',
    lang: 'en',
    text: 'Do not put all your eggs in one basket.',
    tags: ['risk', 'money', 'invest', 'plan', 'backup', 'diversify', 'bet'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-cooks',
    lang: 'en',
    text: 'Too many cooks spoil the broth.',
    tags: ['team', 'meeting', 'people', 'opinion', 'decision', 'cook', 'crowd'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-stitch',
    lang: 'en',
    text: 'A stitch in time saves nine.',
    tags: ['fix', 'bug', 'early', 'maintenance', 'problem', 'later', 'debt'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-rain',
    lang: 'en',
    text: 'It never rains but it pours.',
    tags: ['rain', 'weather', 'problem', 'bad', 'day', 'everything', 'luck'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-grass',
    lang: 'en',
    text: 'The grass is always greener on the other side.',
    tags: ['envy', 'job', 'change', 'compare', 'neighbour', 'want', 'better'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-apple',
    lang: 'en',
    text: 'An apple a day keeps the doctor away.',
    tags: ['health', 'food', 'doctor', 'apple', 'diet', 'fruit', 'sick'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-cat',
    lang: 'en',
    text: 'When the cat is away, the mice will play.',
    tags: ['cat', 'boss', 'holiday', 'away', 'office', 'mouse', 'freedom'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-dog',
    lang: 'en',
    text: 'You cannot teach an old dog new tricks.',
    tags: ['dog', 'learn', 'change', 'old', 'habit', 'training', 'new'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-money',
    lang: 'en',
    text: 'A fool and his money are soon parted.',
    tags: ['money', 'spend', 'buy', 'shopping', 'fool', 'waste', 'price'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-hunger',
    lang: 'en',
    text: 'Hunger is the best sauce.',
    tags: ['food', 'hungry', 'lunch', 'dinner', 'eat', 'cook', 'taste'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-shop',
    lang: 'en',
    text: 'A man without a smile must not open a shop.',
    tags: ['shop', 'service', 'smile', 'customer', 'mood', 'support', 'client'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-early-bird',
    lang: 'en',
    text: 'The early bird catches the worm.',
    tags: ['morning', 'early', 'coffee', 'alarm', 'wake', 'start', 'first', 'monday'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-friend',
    lang: 'en',
    text: 'A friend in need is a friend indeed.',
    tags: ['friend', 'help', 'trouble', 'support', 'team', 'need'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-silence',
    lang: 'en',
    text: 'Silence is golden.',
    tags: ['silence', 'quiet', 'talk', 'meeting', 'noise', 'argument'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-haste',
    lang: 'en',
    text: 'More haste, less speed.',
    tags: ['hurry', 'fast', 'deadline', 'rush', 'mistake', 'slow', 'urgent'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-plan',
    lang: 'en',
    text: 'If you fail to plan, you are planning to fail.',
    tags: ['plan', 'fail', 'prepare', 'project', 'roadmap', 'strategy'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-home',
    lang: 'en',
    text: 'There is no place like home.',
    tags: ['home', 'travel', 'holiday', 'trip', 'return', 'house'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-book',
    lang: 'en',
    text: 'Do not judge a book by its cover.',
    tags: ['book', 'judge', 'appearance', 'design', 'first', 'impression', 'look'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-beggars',
    lang: 'en',
    text: 'Beggars cannot be choosers.',
    tags: ['choice', 'option', 'budget', 'cheap', 'compromise', 'accept'],
    attribution: proverb('en'),
  },
  {
    id: 'en-proverb-laugh',
    lang: 'en',
    text: 'He who laughs last laughs longest.',
    tags: ['laugh', 'joke', 'funny', 'win', 'revenge', 'patience'],
    attribution: proverb('en'),
  },
];
